import { useEffect } from 'react';

/**
 * Hook to prevent zooming in PWA applications
 * Handles keyboard shortcuts (Ctrl/Cmd + Plus/Minus/0) and touch gestures
 */
export function usePreventZoom() {
  useEffect(() => {
    // Prevent zoom with keyboard shortcuts (Ctrl/Cmd + Plus/Minus/0)
    const handleKeydown = (e: KeyboardEvent) => {
      // Check for Ctrl (Windows/Linux) or Cmd (Mac)
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      if (isCtrlOrCmd) {
        // Prevent zoom in (Ctrl/Cmd + Plus)
        if (
          e.key === '+' ||
          e.key === '=' ||
          e.keyCode === 187 ||
          e.keyCode === 171
        ) {
          e.preventDefault();
          return false;
        }

        // Prevent zoom out (Ctrl/Cmd + Minus)
        if (
          e.key === '-' ||
          e.key === '_' ||
          e.keyCode === 189 ||
          e.keyCode === 173
        ) {
          e.preventDefault();
          return false;
        }

        // Prevent reset zoom (Ctrl/Cmd + 0)
        if (e.key === '0' || e.keyCode === 48 || e.keyCode === 96) {
          e.preventDefault();
          return false;
        }
      }
    };

    // Prevent zoom with mouse wheel + Ctrl/Cmd
    const handleWheel = (e: WheelEvent) => {
      // Check if Ctrl or Cmd is pressed
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        return false;
      }
    };

    // Prevent pinch-to-zoom on touchpad and touchscreen
    const handleGestureStart = (e: Event) => {
      e.preventDefault();
    };

    const handleGestureChange = (e: Event) => {
      e.preventDefault();
    };

    const handleGestureEnd = (e: Event) => {
      e.preventDefault();
    };

    // Variables to track touch points for pinch detection
    let lastTouchEnd = 0;

    // Prevent pinch-to-zoom with touch events
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        // Multiple fingers detected, prevent default
        e.preventDefault();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        // Pinch gesture detected
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const now = Date.now();
      // Prevent double-tap zoom
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    // Add event listeners
    document.addEventListener('keydown', handleKeydown, { passive: false });
    document.addEventListener('wheel', handleWheel, { passive: false });

    // Gesture events (Safari)
    document.addEventListener('gesturestart', handleGestureStart, {
      passive: false,
    });
    document.addEventListener('gesturechange', handleGestureChange, {
      passive: false,
    });
    document.addEventListener('gestureend', handleGestureEnd, {
      passive: false,
    });

    // Touch events
    document.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });

    // Prevent browser zoom on Ctrl + Scroll
    const preventCtrlScroll = (e: Event) => {
      // For gesture events that have scale property
      const gestureEvent = e as unknown as { scale?: number };
      if (gestureEvent.scale && gestureEvent.scale !== 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', preventCtrlScroll, {
      passive: false,
    });

    // Cleanup event listeners
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('gesturestart', handleGestureStart);
      document.removeEventListener('gesturechange', handleGestureChange);
      document.removeEventListener('gestureend', handleGestureEnd);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', preventCtrlScroll);
    };
  }, []);
}

/**
 * Additional utility to programmatically reset any zoom if it occurs
 * This can be called when needed to ensure the viewport is at 100%
 */
export function resetViewportScale() {
  // Find the viewport meta tag
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    // Reset the viewport to ensure scale is 1.0
    viewport.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
    );

    // Force a re-render by temporarily changing and restoring the content
    setTimeout(() => {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
      );
    }, 10);
  }

  // Also try to reset using document.body.style
  document.body.style.zoom = '1';

  // For webkit browsers
  if (document.body.style.webkitTransform !== undefined) {
    document.body.style.webkitTransform = 'scale(1)';
  }

  // For other browsers
  if (document.body.style.transform !== undefined) {
    document.body.style.transform = 'scale(1)';
  }
}
